import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Tag, message, Row, Col, Card, Statistic, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import request from '../utils/request';

const Accounts: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [pwdModal, setPwdModal] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  // 三个写操作的在途锁：新增账号 / 重置密码（Modal 的 onOk 不感知 promise）+ 行内启停
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  // 可分配角色（来自角色管理，动态）：{ id, name, code, builtIn }
  const [roleOptions, setRoleOptions] = useState<any[]>([]);
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/admin/account', { params: { page: p, pageSize: 10 } });
      setList(res.list);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [page]);

  // 顶部业务统计（挂号/订单/反馈/用户）
  useEffect(() => {
    request.get('/dashboard/summary').then((res: any) => setSummary(res)).catch(() => {});
  }, []);

  // 拉取可分配角色（仅超管可达本页）
  useEffect(() => {
    request.get('/admin/role/options').then((res: any) => setRoleOptions(res || [])).catch(() => {});
  }, []);

  const handleCreate = async () => {
    const values = await form.validateFields();
    if (creating) return;
    setCreating(true);
    try {
      await request.post('/admin/account', values);
      message.success('创建成功');
      setModalOpen(false);
      form.resetFields();
      fetchList();
    } finally {
      setCreating(false);
    }
  };

  // 禁用/启用账号会直接影响对方能否登录，重复点击可能把状态翻回原值，需锁住
  const toggleStatus = async (record: any) => {
    if (busyId !== null) return;
    setBusyId(record.id);
    try {
      await request.put(`/admin/account/${record.id}`, { status: record.status === 1 ? 0 : 1 });
      message.success(record.status === 1 ? '已禁用' : '已启用');
      fetchList();
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPwd = async () => {
    const values = await pwdForm.validateFields();
    if (resetting) return;
    setResetting(true);
    try {
      await request.put(`/admin/account/${pwdModal.id}`, { password: values.password });
      message.success('密码已重置');
      setPwdModal(null);
      pwdForm.resetFields();
    } finally {
      setResetting(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '用户名', dataIndex: 'username' },
    { title: '姓名', dataIndex: 'name' },
    { title: '角色', dataIndex: 'roleName', width: 130, render: (v: string, r: any) => (r.role === 'SUPER_ADMIN' ? <Tag color="red">{v || '超级管理员'}</Tag> : <Tag>{v || '未分配'}</Tag>) },
    {
      title: '状态', dataIndex: 'status', width: 80,
      render: (v: number) => (v === 1 ? <Tag color="success">启用</Tag> : <Tag color="default">禁用</Tag>),
    },
    {
      title: '操作', width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Button
            size="small"
            loading={busyId === record.id}
            disabled={busyId !== null && busyId !== record.id}
            onClick={() => toggleStatus(record)}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Button size="small" onClick={() => setPwdModal(record)}>重置密码</Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>账号管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          添加账号
        </Button>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card size="small"><Statistic title="挂号总数" value={summary?.registrationCount ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="今日挂号" value={summary?.todayRegistration ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="订单总数" value={summary?.orderCount ?? 0} /></Card></Col>
        <Col span={6}><Card size="small"><Statistic title="用户总数" value={summary?.userCount ?? 0} /></Card></Col>
      </Row>
      <Table columns={columns} dataSource={list} rowKey="id" loading={loading}
        pagination={{ current: page, total, pageSize: 10, onChange: setPage }} />

      <Modal title="添加账号" open={modalOpen} onOk={handleCreate}
        onCancel={() => !creating && setModalOpen(false)}
        okButtonProps={{ loading: creating }}>
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="姓名" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label="密码" rules={[{ required: true, min: 6 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="roleId" label="角色" extra="决定该账号登录后可见的菜单；超管角色恒见全部">
            <Select
              placeholder="选择角色"
              options={roleOptions.map((r) => ({
                value: r.id,
                label: r.builtIn ? `${r.name}（内置）` : r.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`重置密码（${pwdModal?.username || ''}）`} open={!!pwdModal}
        onOk={handleResetPwd} onCancel={() => !resetting && setPwdModal(null)}
        okButtonProps={{ loading: resetting }}>
        <Form form={pwdForm} layout="vertical">
          <Form.Item name="password" label="新密码" rules={[{ required: true, min: 6 }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Accounts;
